import { useCallback, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { Box, Typography, Checkbox, FormControlLabel } from "@mui/material"
import { omit } from "lodash"
import { OptimizeFormApi, useFormContext } from "connected-components/optimize-experiments/form-context"
import { useAccountAlias } from "resources/account"
import { useSavedFilterSearches, type SecuritySearchFilter } from "resources/filters"
import { Creators as DataCreators } from "model/data"
import { OK, ERROR } from "helpers/data-statuses"
import { FILTER_ACTIONS, FILTER_GROUPS } from "../../constants"
import FilterAutocomplete from "./filter-autocomplete"
import CreateCustomSearch from "./create-custom-search"
import "./style.scss"

type LongShort = "long" | "short"

export interface ToggleFiltersOpts {
  longOrShort?: LongShort | null
  open?: boolean
}

export interface FilterOption {
  label: string
  value: string
  group?: string
  action?: (longOrShort?: LongShort | null) => void
  nbFilters?: number
  count?: number
  disabled?: boolean
  filter?: SecuritySearchFilter
}

interface UniverseSelectorProps {
  modelId: string
}

export default function UniverseSelector({ modelId }: UniverseSelectorProps) {
  const [localShortValue, setLocalShortValue] = useState<FilterOption | null>(null)
  const form: OptimizeFormApi = useFormContext()
  const account = useAccountAlias()
  const dispatch = useDispatch()
  const formLongValue = form.getFieldValue("universe.long")
  const formShortValue = form.getFieldValue("universe.short")
  const [savedFilterSearches, savedFilterSearchesStatus] = useSavedFilterSearches(modelId)
  const [isApplyLongToShortChecked, setIsApplyLongToShortChecked] = useState<boolean | null>(null)
  const [isCreateCustomSearchOpen, setIsCreateCustomSearchOpen] = useState<boolean>(false)
  const [isCustomSearchLongOrShort, setIsCustomSearchLongOrShort] = useState<LongShort | null>(null)
  const applyLongToShort = useMemo(() => {
    if (isApplyLongToShortChecked !== null) {
      return isApplyLongToShortChecked
    }
    if (!formLongValue || !formShortValue) return false
    return formLongValue.value === formShortValue.value
  }, [formLongValue, formShortValue, isApplyLongToShortChecked])

  const setBothUniverseValues = useCallback((value: FilterOption | null) => {
    form.setFieldValue("universe.long", value)
    form.setFieldValue("universe.short", value)
  }, [form])

  const handleRefreshSavedFilterSearches = useCallback(() => {
    dispatch(DataCreators.resetCollections([`savedFilterSearches-${account}-${modelId}`]))
  }, [dispatch, account, modelId])

  const toggleFilters = useCallback((opts?: Partial<ToggleFiltersOpts>) => {
    const { longOrShort, open } = opts ?? {}

    // If `open` is a boolean, set it; otherwise toggle.
    setIsCreateCustomSearchOpen(prev => (typeof open === "boolean" ? open : !prev))

    // Only change long/short when explicitly provided.
    if (longOrShort !== undefined) {
      setIsCustomSearchLongOrShort(longOrShort)
    }
  }, [setIsCreateCustomSearchOpen, setIsCustomSearchLongOrShort])

  const filtersList: FilterOption[] = useMemo(() => {
    const createCustomSearchOption: FilterOption = {
      label: "Create Custom Search",
      value: FILTER_ACTIONS.CREATE_CUSTOM_SEARCH,
      group: "",
      action: (longOrShort?: LongShort | null) => toggleFilters({ longOrShort: longOrShort ?? null }),
    }
    const list: FilterOption[] = [createCustomSearchOption]
    if (savedFilterSearchesStatus === OK && savedFilterSearches && savedFilterSearches.length > 0) {
      list.push(...savedFilterSearches.map(search => ({
        ...omit(search, ["accountId", "id", "name", "updatedAt"]),
        label: search.name,
        value: search.id,
        group: FILTER_GROUPS.SAVED_SEARCHES,
      })))
    }
    return list
  }, [savedFilterSearches, savedFilterSearchesStatus, toggleFilters])

  const getFieldValue = useCallback((field: "long" | "short"): FilterOption | null => {
    const fieldValue = field === "long" ? formLongValue : formShortValue
    if (!fieldValue) return null
    return filtersList.find(filter => filter.value === fieldValue.value) || null
  }, [formLongValue, formShortValue, filtersList])

  const longValue = useMemo(() => getFieldValue("long"), [getFieldValue])
  const shortValue = useMemo(() => getFieldValue("short"), [getFieldValue])

  const handleApplyLongToShort = useCallback((checked: boolean) => {
    setIsApplyLongToShortChecked(checked)
    if (checked) {
      setLocalShortValue(getFieldValue("short"))
      setBothUniverseValues(getFieldValue("long"))
    } else {
      if (localShortValue) {
        form.setFieldValue("universe.short", localShortValue)
      }
      setLocalShortValue(null)
    }
  }, [form, getFieldValue, setBothUniverseValues, localShortValue])

  const handleSelectUniverseFilter = useCallback((filter: FilterOption | null, type: "long" | "short") => {
    if (filter && filter.action) {
      filter.action(type)
      return
    }

    if (applyLongToShort) {
      setBothUniverseValues(filter)
    } else {
      form.setFieldValue(`universe.${type}`, filter)
      if (type === "short") {
        setLocalShortValue(filter)
      }
    }
    if (type === "long" && !filter) {
      setIsApplyLongToShortChecked(false)
      if (localShortValue) {
        form.setFieldValue("universe.short", localShortValue)
      }
      setLocalShortValue(null)
    }
  }, [form, applyLongToShort, setBothUniverseValues, localShortValue])

  const handleApplyNewCustomSearch = useCallback((filter: FilterOption | null) => {
    if (!isCustomSearchLongOrShort || !filter) return
    handleRefreshSavedFilterSearches()
    handleSelectUniverseFilter(filter, isCustomSearchLongOrShort)
    setIsCustomSearchLongOrShort(null)
  }, [handleSelectUniverseFilter, isCustomSearchLongOrShort, handleRefreshSavedFilterSearches])

  return <Box className="components_universe-selection">
    <Typography className="universe-selection-label">
      Trade Universe
    </Typography>
    <Typography variant="caption" className="universe-selection-description">
      Extend the set of securities considered for the target portfolio.
    </Typography>
    <CreateCustomSearch
      toggleFilters={toggleFilters}
      showFilters={isCreateCustomSearchOpen}
      applyNewCustomSearch={handleApplyNewCustomSearch}
    />
    {savedFilterSearchesStatus === ERROR ? (
      <Typography color="error" variant="body2">
        Failed to load saved filter searches. Please try refreshing.
      </Typography>
    ) : (
      <div className="basket-types-container">
        <div className="long-basket-row">
          <FilterAutocomplete
            label="Long"
            options={filtersList}
            onChange={(filter) => handleSelectUniverseFilter(filter, "long")}
            value={longValue}
            onMenuOpen={handleRefreshSavedFilterSearches}
          />
          <div className="checkbox-container">
            <FormControlLabel
              slotProps={{
                typography: {
                  className: "checkbox-label",
                },
              }}
              control={
                <Checkbox
                  disabled={!longValue}
                  size="small"
                  checked={applyLongToShort}
                  onChange={(e) => handleApplyLongToShort(e.target.checked)}
                />
              }
              label="Apply Long selection to Short"
            />
          </div>
        </div>
        <FilterAutocomplete
          label="Short"
          options={filtersList}
          applyLongToShort={applyLongToShort}
          overrideValue={applyLongToShort ? longValue : null}
          onChange={(filter) => handleSelectUniverseFilter(filter, "short")}
          onMenuOpen={handleRefreshSavedFilterSearches}
          value={applyLongToShort ? longValue : shortValue}
        />
      </div>
    )}
  </Box>
}
